import React, { useState } from 'react';
import { supabase } from '../supabase';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import LogoDisplay from '../components/LogoDisplay';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password: password,
      });

      if (error) throw error;
      
    } catch (error) {
      console.error("Error de autenticación:", error);
      let errorText = 'Error al iniciar sesión';
      
      if (error.message === 'Invalid login credentials') {
        errorText = 'Credenciales incorrectas. Verifica tu correo y contraseña.';
      } else if (error.message.includes('Email not confirmed')) {
        errorText = 'Por favor, confirma tu correo electrónico o desactiva la confirmación en Supabase.';
      } else {
        errorText = error.message;
      }

      setMessage({ type: 'error', text: errorText });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-12">
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }} 
        className="max-w-md w-full bg-white rounded-3xl shadow-2xl border border-gray-100 overflow-hidden"
      >
        <div className="bg-emerald-900 p-10 text-center relative overflow-hidden">
          <div className="absolute top-0 right-0 -mr-8 -mt-8 w-32 h-32 bg-emerald-800 rounded-full opacity-50 border-4 border-emerald-700" />
          <div className="relative z-10">
            <div className="bg-white w-24 h-24 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-xl ring-4 ring-emerald-800/50 overflow-hidden p-3">
              <LogoDisplay className="w-full h-full object-contain" />
            </div>
            <h2 className="text-2xl font-black text-white tracking-tight">Acceso Ejecutivo</h2>
            <p className="text-emerald-400 text-sm mt-1 font-medium italic">Arboleda Bienes Raíces</p>
          </div>
        </div>

        <form onSubmit={handleLogin} className="p-8 space-y-6">
          <AnimatePresence mode="wait">
            {message.text && (
              <motion.div 
                initial={{ opacity: 0, height: 0 }} 
                animate={{ opacity: 1, height: 'auto' }} 
                exit={{ opacity: 0, height: 0 }}
                className={`p-4 rounded-xl text-xs font-bold text-center flex items-center justify-center gap-2 border ${
                  message.type === 'error' ? 'bg-red-50 text-red-600 border-red-100' : 'bg-emerald-50 text-emerald-700 border-emerald-100'
                }`}
              >
                <SafeIcon icon={message.type === 'error' ? FiIcons.FiAlertCircle : FiIcons.FiCheckCircle} />
                {message.text}
              </motion.div>
            )}
          </AnimatePresence>

          <div className="space-y-4">
            <div>
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1">Correo Corporativo</label>
              <div className="relative mt-1">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                  <SafeIcon icon={FiIcons.FiMail} />
                </div>
                <input 
                  type="email" 
                  required 
                  value={email} 
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:bg-white outline-none transition-all text-sm"
                  placeholder="ejemplo@arboleda.hn"
                />
              </div>
            </div>

            <div>
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1">Contraseña</label>
              <div className="relative mt-1">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                  <SafeIcon icon={FiIcons.FiShield} />
                </div>
                <input 
                  type="password" 
                  required 
                  value={password} 
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:bg-white outline-none transition-all text-sm"
                  placeholder="••••••••"
                />
              </div>
            </div>
          </div>

          <button 
            type="submit" 
            disabled={loading}
            className="w-full bg-emerald-700 hover:bg-emerald-800 text-white font-black py-4 rounded-xl shadow-lg shadow-emerald-700/20 active:scale-[0.98] transition-all disabled:opacity-50 flex items-center justify-center gap-2 uppercase tracking-widest text-xs"
          >
            {loading ? (
              <SafeIcon icon={FiIcons.FiLoader} className="animate-spin text-xl" />
            ) : (
              <><SafeIcon icon={FiIcons.FiLogIn} /> Entrar al Sistema</>
            )}
          </button>
        </form>
      </motion.div>
    </div>
  );
};

export default Login;